import { useAppContext } from '@/context';
import { logout, updateTokenInStorage, updateUserInStorage } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';
import { synchronizeLanguage, hasUrlLanguagePrefix } from '@/utils/languages';
import { setBugsnagUser } from '@/utils/bugsnag';
import { redirectClientToOmniIfNeeded } from '@/utils/redirectOmni';

import { useAuth } from './useAuth';
import { useMutation } from './useMutation';

interface UseJwtVerifyProps {
  handleSuccess: TypeAny;
  handleError?: TypeAny;
  jwt: string;
}

export function useJwtVerify({ handleSuccess, handleError, jwt }: UseJwtVerifyProps) {
  const { setAuthData } = useAuth();
  const { setAlert } = useAppContext();

  const {
    isLoading,
    data = {},
    mutate,
    error
  } = useMutation({
    mutationKey: 'jwt-verify',
    mutationFn: async () =>
      getJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/user`,
        token: jwt
      }).then((response: Api.UserObject | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response) {
          setAlert(response.code);
          return {};
        }

        if (response.user_id) {
          updateTokenInStorage(jwt);
          updateUserInStorage(response);
          setAuthData({ token: jwt, user: response });

          // Update Bugsnag user identification
          setBugsnagUser(response);

          // Update language if user has a language preference and no URL language is specified
          if (!hasUrlLanguagePrefix() && response.language) {
            synchronizeLanguage(response.language, 'jwt-verify');
          }

          redirectClientToOmniIfNeeded(response);
        }

        return response;
      }),
    onSuccess: (response: Api.UserObject) => handleSuccess(response),
    onError: handleError
  });

  return {
    isLoading,
    mutate,
    error,
    data
  };
}
